
import { useEffect, useRef, useState } from 'react';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';

function useInView(threshold = 0.1) {
  const ref = useRef<HTMLDivElement>(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
        }
      },
      { threshold }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [threshold]);

  return { ref, isInView };
}

export default function AboutPage() {
  const [heroLoaded, setHeroLoaded] = useState(false);
  const companySection = useInView(0.2);
  const expertiseSection = useInView(0.2);
  const commitmentSection = useInView(0.2);

  useEffect(() => {
    const timer = setTimeout(() => setHeroLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative h-[400px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/b5dfe54209fab86290126797eff6b692.jpg"
            alt="About Bromine Systems"
            className={`w-full h-full object-cover object-center transition-transform duration-[2000ms] ease-out ${heroLoaded ? 'scale-100' : 'scale-110'}`}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>
        <div className={`relative z-10 text-center text-white px-6 transition-all duration-1000 ease-out ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <h1 className="text-5xl font-bold tracking-tight">About Us</h1>
        </div>
        
        {/* Animated scroll indicator */}
        <div className={`absolute bottom-8 left-1/2 -translate-x-1/2 transition-all duration-1000 delay-700 ${heroLoaded ? 'opacity-100' : 'opacity-0'}`}>
          <div className="w-6 h-10 border-2 border-white/60 rounded-full flex justify-center">
            <div className="w-1.5 h-3 bg-white/80 rounded-full mt-2 animate-bounce"></div>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="space-y-12 text-gray-700 text-base leading-relaxed">
            {/* Our Company */}
            <div 
              ref={companySection.ref}
              className={`transition-all duration-1000 ease-out ${companySection.isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-6 relative inline-block">
                Our Company
                <span className={`absolute -bottom-1 left-0 h-1 bg-teal-600 rounded-full transition-all duration-700 delay-300 ${companySection.isInView ? 'w-full' : 'w-0'}`}></span>
              </h2>
              <p className={`mb-4 transition-all duration-700 delay-200 ${companySection.isInView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
                Bromine Systems Inc. is a leading provider of water disinfection solutions for the marine industry. With decades of experience, we specialize in the production and distribution of bromine delivery systems and bromine cartridges designed to create safe, potable drinking water in challenging marine environments.
              </p>
              <p className={`transition-all duration-700 delay-300 ${companySection.isInView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
                Our commitment to excellence and innovation has made us a trusted partner for organizations worldwide, including the <strong className="text-teal-700">U.S. Navy</strong>, <strong className="text-teal-700">U.S. Coast Guard</strong>, <strong className="text-teal-700">U.S. Army Corps of Engineers</strong>, and numerous <strong className="text-teal-700">offshore oil and gas platforms</strong>.
              </p>
            </div>

            {/* Our Expertise */}
            <div 
              ref={expertiseSection.ref}
              className={`transition-all duration-1000 ease-out ${expertiseSection.isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-6 relative inline-block">
                Our Expertise
                <span className={`absolute -bottom-1 left-0 h-1 bg-teal-600 rounded-full transition-all duration-700 delay-300 ${expertiseSection.isInView ? 'w-full' : 'w-0'}`}></span>
              </h2>
              <p className={`mb-4 transition-all duration-700 delay-200 ${expertiseSection.isInView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
                We understand the unique challenges of providing clean, safe drinking water in marine environments. Our products are engineered to deliver consistent, reliable performance even in the most demanding conditions.
              </p>
              <p className={`transition-all duration-700 delay-300 ${expertiseSection.isInView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
                Through continuous research and development, we ensure our bromine-based water treatment solutions meet the highest standards of safety and effectiveness.
              </p>
            </div>

            {/* Our Commitment */}
            <div 
              ref={commitmentSection.ref}
              className={`transition-all duration-1000 ease-out ${commitmentSection.isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-6 relative inline-block">
                Our Commitment
                <span className={`absolute -bottom-1 left-0 h-1 bg-teal-600 rounded-full transition-all duration-700 delay-300 ${commitmentSection.isInView ? 'w-full' : 'w-0'}`}></span>
              </h2>
              <div className={`bg-gradient-to-r from-teal-50 to-white p-6 rounded-lg border-l-4 border-teal-600 transition-all duration-700 delay-200 ${commitmentSection.isInView ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
                <p className="italic">
                  At Bromine Systems Inc., we believe that access to safe drinking water is fundamental. We're dedicated to promoting knowledge about water safety and healthy living, working in partnership with the marine community to bring effective water treatment solutions to people around the world.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
